import * as React from 'react';

import { jFolderList, jFileLittle, resolveFilePath, jArticleView } from 'jamespot-user-api';
import { JRCFolder } from './JRCFolder';
import { JRCList, JRCListProps } from 'components/JRCList/JRCList';
import { JRCButton } from 'components/JRCButton/JRCButton';
import { useIntl } from 'react-intl';

import { JRCFolderBreadcrumb } from './JRCFolderBreadcrumb';

import { inArray } from 'utils/utils.array';

import {
    CSSActionAccessor,
    CSSLoader,
    CSSOpacityFilter,
    CSSPool,
    CSSTitle,
    CSSTitleAccessor,
    CSSEmptyWrapper,
    CSSListNoHead,
    CSSDocument,
    CSSDocumentTitle,
    CSSDocumentOption,
    CSSActionElements,
    CSSIconAccessor,
    CSSSubTitle,
} from './styles';

import { JRCFoldersProps } from './types';
import { JRCFileAvatar } from 'components/JRCFile/JRCFileAvatar';
import { JRCLoader } from 'components/JRCLoader/JRCLoader';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import { FileLittleList } from './components/FileLittleList';
import { inUriArray, updateUriArray } from './utils';

type PoolItemType = {
    parent: string | undefined;
    folders: jFolderList[];
    documents: jArticleView[];
    _attachedFiles?: jFileLittle[];
};

export type EntryType = jFolderList | jArticleView;

export const JRCFolders = ({
    loadDocuments = false,
    allowMultiSelection = true,
    initialFolders = [],
    initialDocuments = [],
    onSelectFolder,
    onSelectDocument,
    handlers,
}: JRCFoldersProps) => {
    const intl = useIntl();

    const root: jFolderList = {
        id: 0,
        uri: 'root/root',
        type: 'rootFolder',
        title: intl.formatMessage({ id: 'GLOBAL_File_Bank' }),
        mainType: 'root',
        _user: {
            id: 0,
            type: 'user',
            uri: '',
            title: '',
            mainType: '',
            _url: '',
            level: 5,
            dateCreation: '',
            dateModified: '',
        },
        dateCreation: '',
        dateModified: '',
        _right: {
            create: false,
            read: false,
            publish: false,
            administrate: false,
            update: false,
            delete: false,
            comment: false,
            member: false,
        },
        _audience: [],
        _url: '',
        _nbItems: 0,
    };

    const [folders, setFolders] = React.useState<jFolderList[]>([]);
    const [documents, setDocuments] = React.useState<jArticleView[]>([]);
    const [pool, setPool] = React.useState<Record<string, PoolItemType>>({});
    const [currentUri, setCurrentUri] = React.useState<string>(root.uri);
    const [entries, setEntries] = React.useState<EntryType[]>([root]);
    const [loading, isLoading] = React.useState<boolean>(false);
    const [files, setFiles] = React.useState<jFileLittle[]>([]);
    const [urisDocument, setUrisDocument] = React.useState<string[]>(initialDocuments);
    const [urisFolder, setUrisFolder] = React.useState<string[]>(initialFolders);

    React.useEffect(() => {
        fetchBanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClickFolder = (entry: jFolderList) => {
        if (!inArray(entries, entry)) {
            setEntries([...entries, entry]);
        }

        isLoading(true);
        if (loadDocuments) {
            fetchAll(entry.uri);
        } else {
            fetchFolders(entry.uri);
        }
    };

    const onClickDocumentAsFolder = (entry: jArticleView) => {
        if (!inArray(entries, entry)) {
            setEntries([...entries, entry]);
        }
        if (!pool[entry.uri]) {
            setPool({
                ...pool,
                [entry.uri]: {
                    parent: entry.uri,
                    folders: [],
                    documents: [],
                    _attachedFiles: entry._attachedFiles,
                },
            });
        }
        setCurrentUri(entry.uri);
    };

    const fetchBanks = async () => {
        if (!handlers) {
            return;
        }
        isLoading(true);
        const bankResponse = await handlers.filebank.getBanks();
        if (bankResponse !== undefined) {
            const bankFolders = bankResponse.map((bank) => {
                return bank.rootFolder;
            });

            setFolders([...folders, ...bankFolders]);
            isLoading(false);
            setPool({
                ...pool,
                ['root/root']: {
                    parent: undefined,
                    folders: bankFolders,
                    documents: [],
                },
            });
        }
    };

    const fetchFolders = async (uri: string) => {
        if (!handlers) {
            return;
        }
        const folderResponse = await handlers.filebank.getFolders(uri);
        if (folderResponse !== undefined && folderResponse.result && !pool[uri]) {
            setFolders([...folders, ...folderResponse.result]);
            isLoading(false);
            setPool({
                ...pool,
                [uri]: {
                    parent: uri,
                    folders: folderResponse.result,
                    documents: [],
                },
            });
        }

        setCurrentUri(uri);
    };

    const fetchAll = async (uri: string) => {
        if (!handlers) {
            return;
        }
        const folderResponse = await handlers.filebank.getFolders(uri);
        if (folderResponse !== undefined && folderResponse.result && !pool[uri]) {
            setFolders([...folders, ...folderResponse.result]);
        }

        const documentResponse = await handlers.filebank.getDocuments(uri);
        if (documentResponse !== undefined && documentResponse.result.data && !pool[uri]) {
            setDocuments([...documents, ...documentResponse.result.data]);
        }

        if (!pool[uri] && folderResponse && documentResponse) {
            setPool({
                ...pool,
                [uri]: {
                    parent: uri,
                    folders: folderResponse.result,
                    documents: documentResponse.result.data,
                },
            });
        }

        setCurrentUri(uri);
        isLoading(false);
    };

    React.useEffect(() => {
        const files: jFileLittle[] = [];

        documents.forEach((document) => {
            files.push(...document._attachedFiles);
        });

        setFiles(files);
    }, [documents]);

    return (
        <>
            <JRCFolderBreadcrumb
                entries={entries}
                onPress={(entry) => {
                    const newArr = [...entries];
                    newArr.length = entries.indexOf(entry) + 1;
                    if (entry.uri !== currentUri) {
                        setEntries(newArr);
                        setCurrentUri(entry.uri);
                    }
                }}></JRCFolderBreadcrumb>
            {loading ? (
                <CSSLoader>
                    <JRCLoader size={'m'} />
                </CSSLoader>
            ) : (
                <></>
            )}
            {!loading && pool[currentUri] ? (
                <Pool
                    initialFolders={urisFolder}
                    initialDocuments={urisDocument}
                    allowMultiSelection={allowMultiSelection}
                    item={pool[currentUri]}
                    onClickFolder={onClickFolder}
                    onClickDocumentAsFolder={onClickDocumentAsFolder}
                    onSelectFolder={
                        onSelectFolder !== undefined
                            ? (uris) => {
                                  if (onSelectFolder) {
                                      onSelectFolder([...folders.filter((folder) => uris.indexOf(folder.uri) !== -1)]);
                                  }
                                  setUrisFolder(uris);
                              }
                            : undefined
                    }
                    onSelectDocument={
                        onSelectDocument !== undefined
                            ? (uris) => {
                                  if (onSelectDocument) {
                                      onSelectDocument([...files.filter((file) => uris.indexOf(file.uri) !== -1)]);
                                  }
                                  setUrisDocument(uris);
                              }
                            : undefined
                    }
                />
            ) : (
                <></>
            )}
        </>
    );
};

type PoolProps = {
    item: PoolItemType;
    initialFolders: string[];
    initialDocuments: string[];
    allowMultiSelection?: boolean;
    onClickFolder: (entry: jFolderList) => void;
    onClickDocumentAsFolder: (entry: jArticleView) => void;
    onSelectDocument?: (uris: string[]) => void;
    onSelectFolder?: (uris: string[]) => void;
};

const Pool = ({
    item,
    initialFolders,
    initialDocuments,
    allowMultiSelection,
    onClickFolder,
    onClickDocumentAsFolder,
    onSelectDocument,
    onSelectFolder,
}: PoolProps) => {
    const intl = useIntl();

    if (item._attachedFiles) {
        return (
            <CSSPool>
                <CSSListNoHead>
                    <FileLittleList
                        files={item._attachedFiles}
                        initialUris={initialDocuments}
                        allowMultiSelection={allowMultiSelection}
                        onSelectFile={onSelectDocument}
                    />
                </CSSListNoHead>
            </CSSPool>
        );
    }

    return (
        <CSSPool>
            <FolderList
                folders={item.folders}
                initialFolders={initialFolders}
                allowMultiSelection={allowMultiSelection}
                onClickFolder={onClickFolder}
                onSelectFolder={onSelectFolder}
            />
            <CSSListNoHead>
                <DocumentList
                    documents={item.documents}
                    initialDocuments={initialDocuments}
                    allowMultiSelection={allowMultiSelection}
                    onClickDocumentAsFolder={onClickDocumentAsFolder}
                    onSelectDocument={onSelectDocument}
                />
            </CSSListNoHead>
            {item.folders.length === 0 && item.documents.length === 0 ? (
                <CSSEmptyWrapper>{intl.formatMessage({ id: 'GLOBAL_No_Result' })}</CSSEmptyWrapper>
            ) : (
                <></>
            )}
        </CSSPool>
    );
};

const FolderListTitle = ({ folder, onClick }: { folder: jFolderList; onClick: () => void }) => {
    return (
        <CSSTitleAccessor onClick={onClick}>
            <JRCFolder color={folder.folderColor ? folder.folderColor : '#ccc'} size={'md'}></JRCFolder>
            <CSSTitle>{folder.title}</CSSTitle>
        </CSSTitleAccessor>
    );
};

type FolderListProps = {
    folders: jFolderList[];
    initialFolders?: string[];
    allowMultiSelection?: boolean;
    onClickFolder: (entry: jFolderList) => void;
    onSelectFolder?: (uris: string[]) => void;
};

const FolderList = ({
    folders,
    initialFolders = [],
    allowMultiSelection = true,
    onClickFolder,
    onSelectFolder,
}: FolderListProps) => {
    const intl = useIntl();
    const [uris, setUris] = React.useState<string[]>(initialFolders);

    React.useEffect(() => {
        if (onSelectFolder) {
            onSelectFolder(uris);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uris]);

    const listProps: JRCListProps<jFolderList> = {
        data: folders,
        columns: [
            {
                Header: intl.formatMessage({ id: 'GLOBAL_Title' }),
                id: 'title',
                accessor: (folder) => (
                    <CSSOpacityFilter opacity={inUriArray(uris, folder.uri) ? 0.4 : 1}>
                        <FolderListTitle folder={folder} onClick={() => onClickFolder(folder)} />
                    </CSSOpacityFilter>
                ),
                width: 400,
                disableSortBy: true,
            },
            {
                Header: ' ',
                id: 'count',
                width: 100,
                disableSortBy: true,
                accessor: (folder) => {
                    return (
                        folder.type === 'folder' && (
                            <CSSActionElements>
                                {folder._nbItems} {intl.formatMessage({ id: 'FOLDER_Nb_Elements' })}
                            </CSSActionElements>
                        )
                    );
                },
            },
            {
                Header: ' ',
                width: 40,
                disableSortBy: true,
                accessor: (folder) => {
                    const variant = inUriArray(uris, folder.uri) ? 'contained' : 'outlined';
                    return onSelectFolder ? (
                        <CSSActionAccessor>
                            <JRCButton
                                onClick={() =>
                                    allowMultiSelection
                                        ? setUris(updateUriArray(uris, folder.uri))
                                        : setUris([folder.uri])
                                }
                                type="button"
                                color="primary"
                                variant={variant}>
                                {intl.formatMessage({ id: 'GLOBAL_Select' })}
                            </JRCButton>
                        </CSSActionAccessor>
                    ) : (
                        <CSSIconAccessor>
                            <JRCIcon name="icon-angle-right" size={32}></JRCIcon>
                        </CSSIconAccessor>
                    );
                },
            },
        ],
        config: {
            manualSortBy: false,
            draggable: false,
        },
    };

    return folders !== undefined && folders.length ? <JRCList {...listProps}></JRCList> : <></>;
};

type DocumentListProps = {
    documents: jArticleView[];
    initialDocuments: string[];
    allowMultiSelection?: boolean;
    onClickDocumentAsFolder: (entry: jArticleView) => void;
    onSelectDocument?: (uris: string[]) => void;
};

const DocumentList = ({
    documents,
    initialDocuments,
    allowMultiSelection = true,
    onClickDocumentAsFolder,
    onSelectDocument,
}: DocumentListProps) => {
    const intl = useIntl();
    const [uris, setUris] = React.useState<string[]>(initialDocuments);

    React.useEffect(() => {
        if (onSelectDocument) {
            onSelectDocument(uris);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uris]);

    const listProps: JRCListProps<jArticleView> = {
        data: documents,
        columns: [
            {
                Header: intl.formatMessage({ id: 'GLOBAL_Title' }),
                id: 'title',
                accessor: (document) => {
                    if (
                        document._attachedFiles &&
                        document._attachedFiles[0] !== undefined &&
                        document._attachedFiles.length === 1
                    ) {
                        return (
                            <CSSDocument>
                                <CSSDocumentTitle>
                                    <CSSOpacityFilter
                                        opacity={inUriArray(uris, document._attachedFiles[0].uri) ? 0.4 : 1}>
                                        <CSSTitleAccessor>
                                            <JRCFileAvatar
                                                uri={resolveFilePath(document._attachedFiles[0])}
                                                size={40}
                                                mimetype={document._attachedFiles[0].mimetype}
                                            />
                                            <CSSTitle>{document._attachedFiles[0].title}</CSSTitle>
                                        </CSSTitleAccessor>
                                    </CSSOpacityFilter>
                                </CSSDocumentTitle>
                                <CSSDocumentOption>
                                    {onSelectDocument ? (
                                        <CSSActionAccessor>
                                            <JRCButton
                                                onClick={() =>
                                                    allowMultiSelection
                                                        ? setUris(updateUriArray(uris, document._attachedFiles[0].uri))
                                                        : setUris([document._attachedFiles[0].uri])
                                                }
                                                type="button"
                                                color="primary"
                                                variant={
                                                    inUriArray(uris, document._attachedFiles[0].uri)
                                                        ? 'contained'
                                                        : 'outlined'
                                                }>
                                                {intl.formatMessage({ id: 'GLOBAL_Select' })}
                                            </JRCButton>
                                        </CSSActionAccessor>
                                    ) : (
                                        <></>
                                    )}
                                </CSSDocumentOption>
                            </CSSDocument>
                        );
                    }

                    return (
                        <>
                            <CSSDocument onClick={() => onClickDocumentAsFolder(document)}>
                                <CSSDocumentTitle>
                                    <CSSTitleAccessor>
                                        <JRCFileAvatar uri={undefined} size={40} mimetype={'undefined'} />
                                        <CSSTitle>
                                            {document.title}
                                            <ArticleNbSelection uris={uris} document={document} />
                                        </CSSTitle>
                                    </CSSTitleAccessor>
                                </CSSDocumentTitle>
                                <CSSActionElements>
                                    {document._attachedFiles.length} {intl.formatMessage({ id: 'ARTICLE_Nb_Elements' })}
                                </CSSActionElements>
                                <CSSIconAccessor>
                                    <JRCIcon name="icon-angle-right" size={32}></JRCIcon>
                                </CSSIconAccessor>
                            </CSSDocument>
                        </>
                    );
                },
                width: 400,
                disableSortBy: true,
            },
        ],
        config: {
            manualSortBy: false,
            draggable: false,
        },
    };

    return documents !== undefined && documents.length ? <JRCList {...listProps}></JRCList> : <></>;
};

export const ArticleNbSelection = ({ uris, document }: { uris: string[]; document: jArticleView }) => {
    const intl = useIntl();
    return (
        <CSSSubTitle>
            {document._attachedFiles.filter((file) => uris.includes(file.uri)).length}{' '}
            {intl.formatMessage({ id: 'ARTICLE_Nb_Selected_Elements' })}
        </CSSSubTitle>
    );
};
